/**
 * Wörter der Kanalpreise: Stand des letzten Auftrags (`ChannelPriceModifierChangeStatus`) und Sätze zu
 * den Codes des Servers (`ChannelPriceModifierPort`, Rückprüfung). Unbekanntes bleibt als Code sichtbar.
 */
import { problemText, type ApiError } from '@/shared/api';

export type ChannelTone = 'green' | 'amber' | 'red' | 'grey';

/** Solange ein Auftrag läuft, ist die Karte gesperrt und fragt alle 1,5 s nach. */
export const PENDING = ['Pending', 'Claimed', 'ReadbackPending'];

export function channelStatus(status: string | null): { label: string; tone: ChannelTone } {
  switch (status) {
    case null:
      return { label: 'Von Channex gelesen', tone: 'green' };
    case 'Succeeded':
      return { label: 'Von Channex bestätigt', tone: 'green' };
    case 'Pending':
      return { label: 'Auftrag wartet', tone: 'amber' };
    case 'Claimed':
      return { label: 'Wird an Channex übertragen', tone: 'amber' };
    case 'ReadbackPending':
      return { label: 'Wartet auf Bestätigung', tone: 'amber' };
    case 'Failed':
      return { label: 'Fehlgeschlagen', tone: 'red' };
    default:
      return { label: status, tone: 'grey' };
  }
}

export const CHANNEL_PROBLEMS: Record<string, string> = {
  channel_modifier_stale:
    'Der Kanalpreis wurde inzwischen geändert – der aktuelle Stand ist geladen.',
  channel_modifier_change_pending: 'Für diesen Kanal läuft schon ein Auftrag.',
  channel_modifier_external_read_only: 'Diese Regel wird extern in Channex gepflegt.',
  channel_modifier_invalid: 'Der Aufschlag muss zwischen 0,01 und 100 % liegen.',
  channel_modifier_not_found: 'Diesen Kanal gibt es für das Apartment nicht mehr.',
  channel_precondition_changed: 'Der Kanal hat sich bei Channex geändert – bitte erneut versuchen.',
  channel_put_outcome_unknown:
    'Ob Channex den Auftrag angenommen hat, ist offen – der Stand wird geprüft.',
  channel_readback_drift: 'Channex zeigt einen anderen Wert als beauftragt.',
  channel_readback_missing: 'Channex hat den Wert noch nicht zurückgemeldet.',
  channel_readback_upstream: 'Channex konnte den Wert nicht zurückmelden.',
  channel_read_missing: 'Der Kanal ließ sich bei Channex nicht lesen.',
  channel_catalog_unavailable: 'Die Kanäle bei Channex sind gerade nicht abrufbar.',
  channel_mapping_ambiguous:
    'Die Zuordnung des Kanals zu den Apartments ist bei Channex nicht eindeutig.',
  channel_mapping_invalid: 'Die Zuordnung des Kanals ist bei Channex unvollständig.',
  channel_settings_masked: 'Channex zeigt die Einstellungen des Kanals nur verdeckt.',
  channel_scope_ambiguous: 'Der Kanal ist bei Channex auch anderen Konten zugeordnet.',
};

/** Fehlercode einer Karte (letzter Auftrag) als Satz; der rohe Code steht daneben. */
export const channelErrorText = (code: string) => CHANNEL_PROBLEMS[code] ?? 'Fehler beim Kanal';

/** `reloaded`: nach dem Fehler ist der Stand neu geladen – die Karte zeigt, was gilt. */
export function channelSaveProblem(problem: ApiError | null, reloaded = false): string | null {
  if (!problem) return null;
  if (problem.status === 0) {
    return reloaded
      ? 'Keine Antwort vom Server – der Stand ist neu geladen, die Karte zeigt, was gilt.'
      : 'Keine Antwort vom Server – ob der Auftrag angekommen ist, ist offen. Der Stand wird neu geladen.';
  }
  return problemText(problem, 'Der Kanalpreis wurde nicht beauftragt.', {
    known: CHANNEL_PROBLEMS,
  });
}

export const channelPreviewProblem = (problem: ApiError | null) =>
  problem &&
  problemText(
    problem,
    'Die Vorschau des Kanalpreises konnte nicht geladen werden. Es wurde nichts beauftragt.',
    { known: CHANNEL_PROBLEMS },
  );
